// src/business/businessRoutes.jsx
import { Routes, Route, Navigate } from "react-router-dom";
import { useBusinessAuth } from "./context/BusinessAuthContext";

import BusinessLayout from "./layout/BusinessLayout";

import BusinessWorkHome from "./pages/BusinessWorkHome";
import BusinessProducts from "./pages/BusinessProducts";
import BusinessReviews from "./pages/BusinessReviews";
import BusinessAddProduct from "./pages/BusinessAddProduct";
import BusinessEditProduct from "./pages/BusinessEditProduct";
import BusinessOrders from "./pages/BusinessOrders";
import BusinessReturnRefunds from "./pages/BusinessReturnRefunds";
import BusinessAnalytics from "./pages/BusinessAnalytics";
import MerchandisingDashboard from "./pages/merchandising/MerchandisingDashboard";
import MerchandisingOpenWork from "./pages/merchandising/MerchandisingOpenWork";
import OrdersDashboard from "./pages/orders/OrdersDashboard";
import OrdersOpenWork from "./pages/orders/OrdersOpenWork";
import OrdersFulfillment from "./pages/orders/OrdersFulfillment";
import FulfillmentTaskPage from "./pages/orders/FulfillmentTaskPage";
import ParcelLabelPrintPage from "./pages/orders/ParcelLabelPrintPage";
import OrdersShipping from "./pages/orders/OrdersShipping";
import OrdersCustodyReviews from "./pages/orders/OrdersCustodyReviews";
import OrderRecordPage from "./pages/orders/OrderRecordPage";
import DeliveryOperations from "./pages/delivery/DeliveryOperations";
import DeliveryProofReview from "./pages/delivery/DeliveryProofReview";
import DeliverySettlementDesk from "./pages/delivery/DeliverySettlementDesk";

// HR Module
import HREmployees from "./pages/hr/HREmployees";
import HREmployeeDetail from "./pages/hr/HREmployeeDetail";
import HRSeparation from "./pages/hr/HRSeparation";
import HRDepartments from "./pages/hr/HRDepartmentsFoundation";
import HRShifts from "./pages/hr/HRShifts";
import HRAttendance from "./pages/hr/HRAttendance";
import HRGeneralSettings from "./pages/hr/HRGeneralSettings";
import HRLeave from "./pages/hr/HRLeave";
import MyPayslips from "./pages/hr/payslips/MyPayslips";
import MyPayslip from "./pages/hr/payslips/MyPayslip";
import MyHRLayout from "./pages/hr/me/MyHRLayout";
import { MyAttendance, MyHROverview, MyLeave, MyOvertime, MyPay, MyRequests, MySchedule } from "./pages/hr/me/MyHRSections";
import HRDeductions from "./pages/hr/HRDeductions";
import HRPayrollLayout from "./pages/hr/payroll/HRPayrollLayout";
import HRPayrollPeriod from "./pages/hr/payroll/HRPayrollPeriod";
import HRPayslip from "./pages/hr/payroll/HRPayslip";
import PaySchedulesList from "./pages/hr/payroll/PaySchedulesList";
import PayScheduleDetail from "./pages/hr/payroll/PayScheduleDetail";
import { PayrollHistory as HRPayrollHistory, PayrollWaiting as HRPayrollWaiting, PayrollWork as HRPayrollWork } from "./pages/hr/payroll/PayrollLanes";
import HRAnalytics from "./pages/hr/HRAnalytics";
import HRDashboard from "./pages/hr/HRDashboard";
import HROpenWork from "./pages/hr/HROpenWork";
import HROnboardingWorkspace from "./pages/hr/HROnboardingWorkspace";

// ERP Modules
import PurchaseRequisitions from "./pages/procurement/PurchaseRequisitions";
import ProcurementDashboard from "./pages/procurement/ProcurementDashboard";
import RFQManagement from "./pages/procurement/RFQManagement";
import PurchaseOrders from "./pages/procurement/PurchaseOrders";
import ProcurementOpenWork from "./pages/procurement/ProcurementOpenWork";
import ProcurementTimeline from "./pages/procurement/ProcurementTimeline";
import SupplierDirectory from "./pages/suppliers/SupplierDirectory";
import InventoryDashboard from "./pages/inventory/InventoryDashboard";
import InventoryOpenWork from "./pages/inventory/InventoryOpenWork";
import StockOverview from "./pages/inventory/StockOverview";
import InventoryMovements from "./pages/inventory/InventoryMovements";
import InventoryReceipts from "./pages/inventory/InventoryReceipts";
import InventoryAdjustments from "./pages/inventory/InventoryAdjustments";
import FinanceDashboard from "./pages/finance/FinanceDashboard";
import FinanceOpenWork from "./pages/finance/FinanceOpenWork";
import FinanceApprovals from "./pages/finance/FinanceApprovals";
import FinancePayables from "./pages/finance/FinancePayables";
import FinanceReceivables from "./pages/finance/FinanceReceivables";
import FinanceDisbursements from "./pages/finance/FinanceDisbursements";
import FinanceCollections from "./pages/finance/FinanceCollections";
import FinanceSalaries from "./pages/finance/FinanceSalaries";
import FinancePayrollLayout from "./pages/finance/payroll/FinancePayrollLayout";
import PayrollToReview from "./pages/finance/payroll/PayrollToReview";
import PayrollToPay from "./pages/finance/payroll/PayrollToPay";
import PayrollHistory from "./pages/finance/payroll/PayrollHistory";
import CorrectionsList from "./pages/hr/adjustments/CorrectionsList";
import CorrectionPage from "./pages/hr/adjustments/CorrectionPage";
import FinanceAnalytics from "./pages/finance/FinanceAnalytics";
import FinanceCodWorkspace from "./pages/finance/FinanceCodWorkspace";
import DeliveryTracking from "./pages/logistics/DeliveryTracking";
import BusinessSettings from "./pages/BusinessSettings";
import BusinessNotifications from "./pages/BusinessNotifications";
import BusinessMessages from "./pages/BusinessMessages";
import AdministrationRoles from "./pages/administration/AdministrationRoles";
import AdministrationActivityLogs from "./pages/administration/AdministrationActivityLogs";
import MerchandisingCategoriesTags from "./pages/merchandising/MerchandisingCategoriesTags";
import HRRecruitment from "./pages/hr/HRRecruitment";
import AdministrationCalendar from "./pages/administration/AdministrationCalendar";
import PayAreaGuard from "./components/PayAreaGuard";

function HRLandingRedirect() {
  const auth = useBusinessAuth();
  const hasPermission = auth?.hasPermission || (() => false);

  if (hasPermission("hr:employees:read")) {
    return <Navigate to="/business/hr/dashboard" replace />;
  }
  // Everyone else lands on their own HR page (schedule, attendance, requests, leave, payslips).
  return <Navigate to="/business/hr/me" replace />;
}

/**
 * BusinessRoutes
 * - Declares all pages under /business/*
 * - Authentication is handled outside by <RequireBusiness>
 * - HR management lives under /business/hr/*
 * - ERP modules: Procurement, Suppliers, Inventory, Finance, Logistics
 */
export default function BusinessRoutes() {
  return (
    <Routes>
      <Route element={<BusinessLayout />}>
        <Route index element={<BusinessWorkHome />} />
        <Route path="dashboard" element={<BusinessWorkHome />} />
        <Route path="activity" element={<Navigate to="/business/administration/activity" replace />} />

        <Route path="administration" element={<Navigate to="/business/administration/roles" replace />} />
        <Route path="administration/roles" element={<AdministrationRoles />} />
        <Route path="administration/activity" element={<AdministrationActivityLogs />} />
        <Route path="administration/calendar" element={<AdministrationCalendar />} />
        {/* Holiday Calendar Phase C: the per-business Holidays page was replaced by Calendar. */}
        <Route path="administration/holidays" element={<Navigate to="/business/administration/calendar" replace />} />

        <Route path="merchandising" element={<Navigate to="/business/merchandising/dashboard" replace />} />
        <Route path="merchandising/dashboard" element={<MerchandisingDashboard />} />
        <Route path="merchandising/open-work" element={<MerchandisingOpenWork />} />
        <Route path="merchandising/categories" element={<MerchandisingCategoriesTags />} />

        <Route path="products" element={<BusinessProducts />} />
        <Route path="products/add" element={<BusinessAddProduct />} />
        <Route path="products/reviews" element={<BusinessReviews />} />
        <Route path="products/:id" element={<BusinessEditProduct />} />

        <Route path="orders/dashboard" element={<OrdersDashboard />} />
        <Route path="orders/open-work" element={<OrdersOpenWork />} />
        <Route path="orders/fulfillment" element={<OrdersFulfillment />} />
        <Route path="orders/fulfillment/:orderId" element={<FulfillmentTaskPage />} />
        <Route path="orders/fulfillment/:orderId/pack" element={<FulfillmentTaskPage step="pack" />} />
        <Route path="orders/fulfillment/:orderId/label" element={<FulfillmentTaskPage step="label" />} />
        <Route path="orders/fulfillment/:orderId/handoff" element={<FulfillmentTaskPage step="handoff" />} />
        <Route path="orders/fulfillment/:orderId/label/print" element={<ParcelLabelPrintPage />} />
        <Route path="orders/shipping" element={<OrdersShipping />} />
        <Route path="orders/custody-reviews" element={<OrdersCustodyReviews />} />
        <Route path="orders" element={<BusinessOrders />} />
        <Route path="orders/record/:orderId" element={<OrderRecordPage />} />
        <Route path="orders/returns" element={<BusinessReturnRefunds />} />
        <Route path="messages" element={<BusinessMessages />} />
        <Route path="orders/analytics" element={<BusinessAnalytics />} />
        <Route path="analytics" element={<Navigate to="/business/orders/analytics" replace />} />

        <Route path="delivery" element={<DeliveryOperations />} />
        <Route path="delivery/proofs" element={<DeliveryProofReview />} />
        {/* WP8 target-v2 (dark): taking a trip's cash, the cases a count opens,
            and money a customer was never handed back. */}
        <Route path="delivery/settlement" element={<DeliverySettlementDesk />} />

        {/* Procurement */}
        <Route path="procurement" element={<Navigate to="/business/procurement/dashboard" replace />} />
        <Route path="procurement/dashboard" element={<ProcurementDashboard />} />
        <Route path="procurement/requisitions" element={<PurchaseRequisitions />} />
        <Route path="procurement/rfqs" element={<RFQManagement />} />
        <Route path="procurement/orders" element={<PurchaseOrders />} />
        <Route path="procurement/suppliers" element={<SupplierDirectory />} />
        <Route path="procurement/deliveries" element={<DeliveryTracking />} />
        <Route path="procurement/open-work" element={<ProcurementOpenWork />} />
        <Route path="procurement/timeline" element={<ProcurementTimeline />} />

        {/* Suppliers */}
        <Route path="suppliers" element={<Navigate to="/business/procurement/suppliers" replace />} />

        {/* Inventory */}
        <Route path="inventory/dashboard" element={<InventoryDashboard />} />
        <Route path="inventory/open-work" element={<InventoryOpenWork />} />
        <Route path="inventory" element={<StockOverview />} />
        <Route path="inventory/movements" element={<InventoryMovements />} />
        <Route path="inventory/receipts" element={<InventoryReceipts />} />
        <Route path="inventory/adjustments" element={<InventoryAdjustments />} />

        {/* Finance */}
        <Route path="finance" element={<Navigate to="/business/finance/dashboard" replace />} />
        <Route path="finance/dashboard" element={<FinanceDashboard />} />
        <Route path="finance/open-work" element={<FinanceOpenWork />} />
        <Route path="finance/approvals" element={<FinanceApprovals />} />
        <Route path="finance/payables" element={<FinancePayables />} />
        <Route path="finance/disbursements" element={<FinanceDisbursements />} />
        <Route path="finance/receivables" element={<FinanceReceivables />} />
        <Route path="finance/collections" element={<FinanceCollections />} />
        <Route path="finance/cod" element={<FinanceCodWorkspace />} />
        <Route path="finance/salaries" element={<PayAreaGuard title="Pay presets" anyOf={["finance:read", "finance:salary_templates:read", "hr:compensation:read", "hr:compensation:manage"]}><FinanceSalaries /></PayAreaGuard>} />
        {/* UI-2: one Finance › Payroll workspace, three real routes. */}
        <Route path="finance/payroll" element={<FinancePayrollLayout />}>
          <Route index element={<PayrollToReview />} />
          <Route path="payments" element={<PayrollToPay />} />
          <Route path="history" element={<PayrollHistory />} />
          {/* Report 54B (D2): corrections to paid payslips, as Finance works them. */}
          <Route path="corrections" element={<CorrectionsList area="finance" />} />
        </Route>
        <Route path="finance/payroll/corrections/:adjustmentId" element={<CorrectionPage area="finance" />} />
        <Route path="finance/analytics" element={<FinanceAnalytics />} />

        {/* Logistics */}
        <Route path="logistics" element={<Navigate to="/business/procurement/deliveries" replace />} />

        {/* Settings */}
        <Route path="settings" element={<BusinessSettings />} />

        {/* Notifications */}
        <Route path="notifications" element={<BusinessNotifications />} />

        {/* HR management */}
        <Route path="hr" element={<HRLandingRedirect />} />
        <Route path="hr/dashboard" element={<HRDashboard />} />
        <Route path="hr/open-work" element={<HROpenWork />} />
        <Route path="hr/invitations" element={<Navigate to="/business/hr/recruitment" replace />} />
        <Route path="hr/recruitment" element={<HRRecruitment view="positions" />} />
        <Route path="hr/recruitment/applicants" element={<HRRecruitment view="applicants" />} />
        <Route path="hr/recruitment/applicants/:applicationId" element={<HRRecruitment view="workspace" />} />
        <Route path="hr/onboarding-queue" element={<Navigate to="/business/hr/recruitment/applicants" replace />} />
        <Route path="hr/onboarding/:memberId" element={<HROnboardingWorkspace />} />
        <Route path="hr/employees/:memberId/separation" element={<HRSeparation />} />
        <Route path="hr/employees/:memberId" element={<HREmployeeDetail />} />
        <Route path="hr/employees/:memberId/:section" element={<HREmployeeDetail />} />
        <Route path="hr/employees" element={<HREmployees />} />
        <Route path="hr/departments" element={<HRDepartments />} />
        <Route path="hr/shifts" element={<HRShifts />} />
        <Route path="hr/attendance" element={<HRAttendance />} />
        <Route path="hr/roles" element={<Navigate to="/business/administration/roles" replace />} />
        <Route path="hr/activity" element={<Navigate to="/business/administration/activity?module=hr" replace />} />
        <Route path="hr/settings" element={<HRGeneralSettings />} />
        <Route path="hr/settings/:section" element={<HRGeneralSettings />} />
        <Route path="hr/leave" element={<HRLeave />} />
        {/* UI-3B: the employee's own payslips, and a page per payslip that can be linked and printed. */}
        <Route path="hr/my-payslips" element={<MyPayslips />} />
        <Route path="hr/my-payslips/:payslipId" element={<MyPayslip />} />
        {/* UI-3C: My HR as one workspace with an address per section. */}
        <Route path="hr/me" element={<MyHRLayout />}>
          <Route index element={<MyHROverview />} />
          <Route path="schedule" element={<MySchedule />} />
          <Route path="attendance" element={<MyAttendance />} />
          <Route path="leave" element={<MyLeave />} />
          <Route path="overtime" element={<MyOvertime />} />
          <Route path="requests" element={<MyRequests />} />
          <Route path="pay" element={<MyPay />} />
          <Route path="*" element={<Navigate to="/business/hr/me" replace />} />
        </Route>
        <Route path="hr/deductions" element={<HRDeductions />} />
        <Route path="hr/deductions/:section" element={<HRDeductions />} />
        <Route path="hr/pay-presets" element={<PayAreaGuard title="Pay presets" anyOf={["hr:compensation:read", "hr:compensation:manage"]}><FinanceSalaries scope="hr" /></PayAreaGuard>} />
        {/* UI-3A: HR › Payroll as one workspace — Work / Waiting on Finance / History — and a page per period. */}
        <Route path="hr/payroll" element={<PayAreaGuard title="Payroll" anyOf={["hr:payroll:read", "hr:payroll:manage"]}><HRPayrollLayout /></PayAreaGuard>}>
          <Route index element={<HRPayrollWork />} />
          <Route path="waiting" element={<HRPayrollWaiting />} />
          <Route path="history" element={<HRPayrollHistory />} />
          <Route path="schedules" element={<PaySchedulesList />} />
          {/* Report 54B (D2): corrections to paid payslips, as HR raises and follows them. */}
          <Route path="corrections" element={<CorrectionsList area="hr" />} />
        </Route>
        <Route path="hr/payroll/corrections/:adjustmentId" element={<PayAreaGuard title="Payroll" anyOf={["hr:payroll:read", "hr:payroll:manage"]}><CorrectionPage area="hr" /></PayAreaGuard>} />
        <Route path="hr/payroll/periods/:periodId" element={<PayAreaGuard title="Payroll" anyOf={["hr:payroll:read", "hr:payroll:manage"]}><HRPayrollPeriod /></PayAreaGuard>} />
        {/* 51C: one stored payslip, read-only, for whoever may read payroll (APP-HR-108). */}
        <Route path="hr/payroll/payslips/:payslipId" element={<PayAreaGuard title="Payroll" anyOf={["hr:payroll:read", "hr:payroll:manage"]}><HRPayslip /></PayAreaGuard>} />
        <Route path="hr/payroll/schedules/:scheduleId" element={<PayAreaGuard title="Payroll" anyOf={["hr:payroll:read", "hr:payroll:manage"]}><PayScheduleDetail /></PayAreaGuard>} />
        <Route path="hr/payroll/schedules/:scheduleId/:section" element={<PayAreaGuard title="Payroll" anyOf={["hr:payroll:read", "hr:payroll:manage"]}><PayScheduleDetail /></PayAreaGuard>} />
        <Route path="hr/analytics" element={<HRAnalytics />} />
        <Route path="hr/records" element={<Navigate to="/business/hr/employees" replace />} />

        {/* Legacy redirect: old /employees -> new HR employees */}
        <Route path="employees" element={<Navigate to="/business/hr/employees" replace />} />

        {/* fallback */}
        <Route path="*" element={<Navigate to="/business" replace />} />
      </Route>
    </Routes>
  );
}
